import React, { useCallback, useLayoutEffect, useRef } from "react";
import { Map, NavigationControl } from "react-map-gl/maplibre";
import type { Map as MaplibreMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { basemapStyle } from "../../config/theme";
import { useTheme } from "../../contexts/ThemeContext";
import MapAttribution from "../common/MapAttribution";
import type { Ring } from "../../utils/polygonEditor";
import type { EditorPolygon, PolygonDrawApi } from "./terraDrawController";

/** What the page can do with the editor. Re-issued through `onReady` whenever the map is rebuilt. */
export type PolygonEditorHandle = Omit<PolygonDrawApi, "destroy">;
export type { EditorMode, EditorPolygon } from "./terraDrawController";

interface View {
  latitude: number;
  longitude: number;
  zoom: number;
}

interface PolygonEditorMapProps {
  initialView: View;
  /** Added once, when the map first loads, and the map is fitted to them. */
  initialRings?: Ring[];
  /** Called when the map first loads and `initialRings` could not all be added. */
  onInitialLoadFailed?: () => void;
  onReady: (handle: PolygonEditorHandle) => void;
  onChange?: (polygons: EditorPolygon[]) => void;
  onSelectionChange?: (id: string | null) => void;
  /** Overlays (toolbar, hints). They sit above the map, inside its box. */
  children?: React.ReactNode;
}

/**
 * The Carto basemap with a terra-draw polygon editor on it. It has its own map rather than the
 * shared one, because that one puts a deck.gl canvas on top that takes the pointer events.
 *
 * A theme change swaps the basemap style, which wipes the editor's layers, so the map is rebuilt:
 * the shapes and the camera are kept in refs and put back on the new map.
 */
const PolygonEditorMap: React.FC<PolygonEditorMapProps> = ({
  initialView,
  initialRings,
  onInitialLoadFailed,
  onReady,
  onChange,
  onSelectionChange,
  children,
}) => {
  const { isDark } = useTheme();
  const themeKey = isDark ? "dark" : "light";

  // Null until the first load; after that always the shapes as of the last change.
  const snapshotRef = useRef<Ring[] | null>(null);
  const cameraRef = useRef<View | null>(null);
  const controllerRef = useRef<PolygonDrawApi | null>(null);
  const generationRef = useRef(0);

  // The latest props, so the load handler (made once per map) never holds stale ones.
  const live = useRef({ initialRings, onInitialLoadFailed, onReady, onChange, onSelectionChange });
  live.current = { initialRings, onInitialLoadFailed, onReady, onChange, onSelectionChange };

  const teardown = useCallback(() => {
    generationRef.current += 1;
    controllerRef.current?.destroy();
    controllerRef.current = null;
  }, []);

  // Rebuilt map (theme change) or unmount: stop the editor on the old one. A layout effect, so it
  // runs before react-map-gl's passive cleanup removes the old map.
  useLayoutEffect(() => teardown, [themeKey, teardown]);

  const handleLoad = useCallback(
    async (map: MaplibreMap, dark: boolean) => {
      teardown();
      const generation = generationRef.current;
      map.on("moveend", () => {
        const c = map.getCenter();
        cameraRef.current = { latitude: c.lat, longitude: c.lng, zoom: map.getZoom() };
      });
      try {
        const { createPolygonDraw } = await import("./terraDrawController");
        if (generation !== generationRef.current) return; // the map went away while loading
        const api = createPolygonDraw({ map, dark });
        controllerRef.current = api;

        api.on("change", () => {
          const polygons = api.getPolygons();
          snapshotRef.current = polygons.map((p) => p.ring);
          live.current.onChange?.(polygons);
        });
        const reportSelection = () => live.current.onSelectionChange?.(api.selectedId());
        api.on("select", reportSelection);
        api.on("deselect", reportSelection);

        if (snapshotRef.current === null) {
          const rings = live.current.initialRings ?? [];
          snapshotRef.current = [];
          if (rings.length > 0) {
            const ids = api.addRings(rings);
            if (ids.length < rings.length) live.current.onInitialLoadFailed?.();
            if (ids.length > 0) api.fitTo(rings);
          }
        } else {
          api.addRings(snapshotRef.current);
        }
        live.current.onReady(api);
      } catch (err) {
        console.error("Could not start the polygon editor:", err);
      }
    },
    [teardown]
  );

  return (
    <div className="relative h-full w-full">
      <Map
        key={themeKey}
        mapStyle={basemapStyle(isDark)}
        initialViewState={cameraRef.current ?? initialView}
        attributionControl={false}
        style={{ width: "100%", height: "100%" }}
        onLoad={(e) => {
          void handleLoad(e.target as unknown as MaplibreMap, isDark);
        }}
      >
        <NavigationControl position="top-right" showCompass={false} />
      </Map>
      {children}
      <MapAttribution />
    </div>
  );
};

export default PolygonEditorMap;
